/**
 * Every field of Majik's recurring jobs, jobs, products and orders
 * (docs/majik/DATA-MODEL-V1.md §2.5, §2.7). Money is described in dollars, as
 * people see it; the database keeps cents.
 */
import type { FieldDef } from '../types.ts'
import { fieldsOf, standardFields } from './define.ts'

const NY_DAY = 'on New York’s calendar'

export const recurringJobFieldDefs: readonly FieldDef[] = [
  ...standardFields('recurringJob', 'recurring job'),
  ...fieldsOf('recurringJob', [
    ['locationId', 'Location', 'id', 'general', 'The record number of the site it cleans.'],
    ['name', 'Name', 'text', 'personal', 'What it is called; usually the location’s name.'],
    ['status', 'Status', 'code', 'general', 'Only active rules make visits — the on/off switch.'],
    ['cadence', 'Cadence', 'code', 'general', 'How often a visit comes round.'],
    [
      'startsOn',
      'First visit',
      'date',
      'general',
      `The series’ first visit, ${NY_DAY}. Every later visit counts from it; a monthly rule keeps its day of the month.`,
    ],
    [
      'startTime',
      'Start time',
      'text',
      'general',
      'The visits’ start on New York’s wall clock. Daylight saving never moves it.',
    ],
    [
      'endDate',
      'End date',
      'date',
      'general',
      `The last day it makes visits, ${NY_DAY}. Empty while it runs on.`,
    ],
    [
      'leadDays',
      'Lead days',
      'count',
      'operations',
      'How many days ahead each visit becomes a real job.',
      'days',
    ],
    [
      'materialisedThrough',
      'Jobs made through',
      'date',
      'operations',
      'The last visit day already made into a job by the automation. Empty before any.',
    ],
    ['workTypeId', 'Work type', 'id', 'general', 'The record number of the kind of work done.'],
    [
      'hours',
      'Hours',
      'number',
      'general',
      'How long each visit takes, in quarter hours.',
      'hours',
    ],
    [
      'billAmount',
      'Bill amount',
      'money',
      'financial',
      'What each visit bills. Empty when a monthly rate covers it.',
      'dollars',
    ],
    ['notes', 'Job notes', 'text', 'personal', 'Copied onto every visit it makes.'],
    [
      'notifyCustomer',
      'Notify customer',
      'flag',
      'general',
      'The customer gets the day-before notice for each visit.',
    ],
    [
      'notifyCleaners',
      'Notify cleaners',
      'flag',
      'general',
      'The cleaners are told about each visit. On unless turned off.',
    ],
    [
      'splitFromId',
      'Split from',
      'id',
      'operations',
      'The rule this one replaced when someone changed “this and following” visits.',
    ],
  ]),
]

export const jobFieldDefs: readonly FieldDef[] = [
  ...standardFields('job', 'job'),
  ...fieldsOf('job', [
    ['locationId', 'Location', 'id', 'general', 'The record number of the site visited.'],
    [
      'recurringJobId',
      'Recurring job',
      'id',
      'general',
      'The record number of the rule that made it. Empty for a one-off.',
    ],
    [
      'occurrenceOn',
      'Visit of',
      'date',
      'operations',
      `The day of the rule’s schedule this job stands for, ${NY_DAY} — kept when the visit moves, so that day is never made into a job twice.`,
    ],
    ['name', 'Job name', 'text', 'personal', 'Usually the location’s name.'],
    [
      'status',
      'Status',
      'code',
      'general',
      'Where the visit stands. Billed jobs show as Completed; where their money is shows in Billing.',
    ],
    ['scheduledAt', 'Scheduled', 'datetime', 'general', 'When the visit starts.'],
    [
      'startTime',
      'Start',
      'text',
      'general',
      'The on-site window’s start on New York’s wall clock, when the visit has one.',
    ],
    [
      'endTime',
      'End',
      'text',
      'general',
      'The on-site window’s end. Earlier than the start means past midnight.',
    ],
    ['hours', 'Hours', 'number', 'general', 'How long the visit takes, in quarter hours.', 'hours'],
    [
      'billAmount',
      'Bill amount',
      'money',
      'financial',
      'What this visit bills. Empty when a monthly rate covers it.',
      'dollars',
    ],
    ['notes', 'Job notes', 'text', 'personal', 'Anything the cleaners or the office should know.'],
    [
      'notifyCustomer',
      'Notify customer',
      'flag',
      'general',
      'The customer gets the day-before notice.',
    ],
    [
      'notifyCleaners',
      'Notify cleaners',
      'flag',
      'general',
      'The cleaners are told when it is made and the day before. On unless turned off.',
    ],
    ['completedAt', 'Completed', 'datetime', 'general', 'When it was marked completed.'],
  ]),
]

export const productFieldDefs: readonly FieldDef[] = [
  ...standardFields('product', 'product'),
  ...fieldsOf('product', [
    ['name', 'Name', 'text', 'general', 'What it is called. No two products share a name.'],
    ['description', 'Description', 'text', 'general', 'The default line description.'],
    ['category', 'Category', 'code', 'general', 'Groups the order picker.'],
    ['type', 'Type', 'code', 'general', 'A supply kept in stock, equipment, or a service.'],
    [
      'unitPrice',
      'Amount',
      'money',
      'financial',
      'What a client is charged for one. Empty for something priced per order.',
      'dollars',
    ],
    ['unitCost', 'Our cost', 'money', 'financial', 'What one costs Majik.', 'dollars'],
    [
      'stock',
      'Stock',
      'count',
      'operations',
      'How many are on the shelf. Kept for supplies only, and never below 0.',
    ],
    ['taxCode', 'Tax', 'code', 'general', 'Whether sales tax applies to it.'],
    [
      'status',
      'Status',
      'code',
      'general',
      'Inactive products leave the menus but stay on old orders; nothing is deleted.',
    ],
    [
      'quickbooksItemName',
      'QuickBooks item',
      'text',
      'operations',
      'The exact item name in QuickBooks, e.g. Supplies:cf.',
    ],
    [
      'quickbooksItemId',
      'QuickBooks item ID',
      'text',
      'operations',
      'The item’s id in QuickBooks.',
    ],
  ]),
]

export const orderFieldDefs: readonly FieldDef[] = [
  ...standardFields('order', 'order'),
  ...fieldsOf('order', [
    [
      'locationId',
      'Delivery location',
      'id',
      'general',
      'The record number of the site it goes to.',
    ],
    ['name', 'Order name', 'text', 'personal', 'Usually the location’s name.'],
    ['status', 'Status', 'code', 'general', 'Where the delivery stands.'],
    ['orderedOn', 'Ordered', 'date', 'general', `The day it was ordered, ${NY_DAY}.`],
    ['deliveredOn', 'Delivered', 'date', 'general', `The day it was delivered, ${NY_DAY}.`],
    [
      'acceptedBy',
      'Accepted by',
      'text',
      'personal',
      'Who took the delivery, as written, e.g. “Arista on 03/19”.',
    ],
    ['notes', 'Notes', 'text', 'personal', 'Anything about the delivery.'],
  ]),
]
